// src/components/EntityDetailsHeader.js
import React from "react";
import { Link } from "react-router-dom";
import { formatDateTime } from "../utils/format";
import { useTranslation } from "react-i18next";

// --- helpers ---
const STATUS_COLORS = {
  ONGOING: "#FEA26F",
  FINISHED: "#06D6A0",
  DELIVERED: "#3872B0",
  PAID: "#0AD69F",
  UNPAID: "#A8ADC3",
  SIGNED: "#06D6A0",
  QUOTE_GENERATED: "#3872B0",
  CREATED: "#A8ADC3",
};

const chipColor = (labelOrKey) => STATUS_COLORS[labelOrKey] || "#A8ADC3";

const formatKm = (n) => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString("fr-FR");
};

const pct = (p) => (p == null || p === "" ? "—" : `${String(p).replace("%", "")}%`);

const DetailsHeader = ({
  entity,
  entityId,
  status = "",
  rawStatus = "",
  client = { code: "", name: "", to: "" },
  complementary = { id: "", to: "" },
  createdAt,
  fuelPercent,
  mileage,
  vehicle = {},
  rightActions,
  paymentMode = null,
  justificationFile = null,
  onPaymentModeClick,
}) => {
  const { t } = useTranslation();
  const statusBg = chipColor(rawStatus || status);

  const StatusChip = () => {
    if (!status) return null;

    return (
      <span
        className="text-[14px] px-2 py-0.5 rounded-[12px] text-white font-extrabold"
        style={{ backgroundColor: statusBg }}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  const TopLine = () => (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      {/* left: title + status */}
      <div className="flex items-center gap-3">
        <h1 className="text-[32px] font-semibold">
          Détails de <span className="text-primary font-bold text-[32px]">{entityId ?? "—"}</span>
        </h1>
        <StatusChip />
      </div>
      {/* right: action buttons */}
      {rightActions ? <div className="flex items-center gap-3 ml-auto">{rightActions}</div> : null}
    </div>
  );

  const SecondaryLine = () => (
    <div className="flex items-center justify-between flex-wrap gap-3">
      {/* left group: client • complementary • createdAt */}
      <div className="flex items-center gap-3 text-[24px] text-primary font-semibold flex-wrap">
        {client?.code || client?.name ? (
          client?.to ? (
            <Link to={client.to} className="underline">
              {client.code ? `${client.code} - ` : ""}
              {client.name || ""}
            </Link>
          ) : (
            <span>
              {client.code ? `${client.code} - ` : ""}
              {client.name || ""}
            </span>
          )
        ) : (
          <span className="text-gray-500">Client —</span>
        )}

        <span className="text-primary">-</span>

        {complementary?.id ? (
          complementary?.to ? (
            <Link to={complementary.to} className="underline">
              {complementary.id}
            </Link>
          ) : (
            <span>{complementary.id}</span>
          )
        ) : (
          <span className="text-gray-500">—</span>
        )}

        <span className="text-primary">-</span>

        <span className="text-[#707070] font-semibold text-[24px]">
          {createdAt ? formatDateTime(createdAt) : "—"}
        </span>
      </div>

      {/* right group: Carburant + Kilométrage + Payment Mode */}
      {(fuelPercent !== null && fuelPercent !== undefined) || (mileage !== null && mileage !== undefined) || paymentMode ? (
        <div className="flex items-center gap-6 text-[16px] text-primary font-semibold">
          {fuelPercent !== null && fuelPercent !== undefined && (
            <span>
              Carburant <span className="text-[#38485C] font-bold">{pct(fuelPercent)}</span>
            </span>
          )}
          {mileage !== null && mileage !== undefined && (
            <span>
              Kilométrage <span className="text-[#38485C] font-bold">{formatKm(mileage)}</span>
            </span>
          )}
          {paymentMode && (
            <span>
              Mode de paiement: {' '}
              {justificationFile && onPaymentModeClick ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Payment mode button clicked', { justificationFile, onPaymentModeClick: !!onPaymentModeClick });
                    if (onPaymentModeClick) {
                      onPaymentModeClick();
                    }
                  }}
                  className="text-[#38485C] font-bold underline hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0"
                >
                  {paymentMode}
                </button>
              ) : (
                <span className="text-[#38485C] font-bold">
                  {paymentMode}
                  {!justificationFile && ' (no justification file)'}
                  {justificationFile && !onPaymentModeClick && ' (no click handler)'}
                </span>
              )}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );

  const VehicleRow = () => {
    if (!vehicle) return null;
    
    return (
      <div className="bg-white rounded-[12px] mt-3 px-6 py-4 shadow-[0px_6px_26px_#626B7D12] grid grid-cols-1 md:grid-cols-6 gap-4">
        <Field label="MARQUE" value={vehicle.brand || '—'} />
        <Field label="MODEL" value={vehicle.model || '—'} />
        <Field label="TYPE" value={vehicle.type || '—'} />
        <Field label="COULEUR" value={vehicle.color || '—'} />
        <Field label="IMMATRICULATION" value={vehicle.plate || '—'} />
        <Field label="VIN" value={vehicle.vin || '—'} />
      </div>
    );
  };

  return (
    <div className="mt-2">
      <TopLine />
      <SecondaryLine />
      <VehicleRow />
    </div>
  );
};

const Field = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[14px] font-semibold tracking-wide text-[#707070]">{label}</span>
    <span className={`text-[20px] font-semibold text-[#38485C]`}>
      {value || "—"}
    </span>
  </div>
);

export default DetailsHeader;
